import React, {useContext, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import axios from "axios";
import {API_URL} from "../constants";
import {AuthContext} from "./AuthContext";
import {AXIOS_NOT_FOUND_REDIRECT_OPT_OUT} from "../axiosNotFoundRedirect";

const Header = () => {
    const {token, setToken, admin, setAdmin, hasRole, hasPermission} = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const isDispatchPage = location.pathname.replace(/\/+$/, '') === '/dispatch';
    const navigate = useNavigate();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const toggle = () => setIsOpen(!isOpen);
    const closeMenu = () => setIsOpen(false);

    const handleMenuClick = (event) => {
        const link = event.target.closest("a");

        if (link && !link.classList.contains("dropdown-toggle")) {
            closeMenu();
        }
    };

    const onLogout = async () => {
        localStorage.removeItem("authToken");

        setIsLoggingOut(true); // Set logging out to true
        try {
            // Make an API call to logout from the backend
            await axios.post(
                `${API_URL}/api/admin/logout`,
                {},
                {
                    ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // Clear the token from local storage (or wherever it's stored)
            localStorage.removeItem("authToken");
            // Update the token in the AuthContext
            setToken(null);
            setAdmin(null);

            // Redirect the user to the login or home page
            navigate("/");
        } catch (error) {
            console.error("Failed to logout:", error);
            localStorage.removeItem("authToken");
            setToken(null);
            setAdmin(null);
            navigate("/");
        } finally {
            setIsLoggingOut(false);
        }
    };

    const getInitials = (name) => {
        return name
            ? name
                .split(" ")
                .map((part) => part[0])
                .join("")
                .toUpperCase()
            : "";
    };

    return (
        <nav className="navbar navbar-expand-lg navbar-light bg-light px-2 admin-navbar admin-navbar-compact">
            <Link className="navbar-brand" to="/" onClick={closeMenu}>
                <img
                    src="https://file-uploads-navi.s3.ca-central-1.amazonaws.com/logos/NaviPickups.png"
                    height="50"
                    alt=""
                />
            </Link>
            {token ? (
                <>
                    {isOpen && <div className="admin-menu-backdrop" onClick={closeMenu}/>}
                    <div
                        className={`collapse navbar-collapse admin-navbar-menu ${isOpen ? "show" : ""}`}
                        id="adminNavbarMenu"
                        onClick={handleMenuClick}
                    >
                        <ul className="navbar-nav admin-main-menu">
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === "/dashboard" ? "active" : ""}`} to="/dashboard">
                                    Dashboard
                                </Link>
                            </li>
                            {hasRole("super-admin") && (
                                <li className="nav-item">
                                    <Link className={`nav-link ${location.pathname.startsWith("/whitelabels") ? "active" : ""}`} to="/whitelabels">
                                        Whitelabels
                                    </Link>
                                </li>
                            )}
                            {hasPermission("pickup-view") && (
                                <li className="nav-item dropdown">
                                    <a
                                        className={`nav-link dropdown-toggle ${location.pathname.startsWith("/pickups") ? "active" : ""}`}
                                        href="#"
                                        id="navbarDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >Deliveries</a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        <li>
                                            <Link className="dropdown-item" to="/pickups/live-requests">
                                                Live Requests
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/pickups">
                                                Delivery List
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/pickups/statistics/by_day">
                                                Statistics by Day
                                            </Link>
                                        </li>
                                        {hasPermission("sales-view") && (
                                            <li>
                                                <Link className="dropdown-item" to="/pickups/sales">
                                                    Sales
                                                </Link>
                                            </li>
                                        )}
                                        {hasPermission("feedback-view") && (
                                            <li>
                                                <Link className="dropdown-item" to="/feedbacks">
                                                    Feedbacks
                                                </Link>
                                            </li>
                                        )}
                                    </ul>
                                </li>
                            )}
                            {hasPermission("driver-view") && (
                                <li className="nav-item dropdown">
                                    <a
                                        className={`nav-link dropdown-toggle ${location.pathname.startsWith("/drivers") || location.pathname.startsWith("/vehicles") ? "active" : ""}`}
                                        href="#"
                                        id="navbarDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Drivers
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        {hasPermission("driver-view") && hasPermission("pickup-view") && (
                                            <li className="nav-item">
                                                <Link className={`dropdown-item ${isDispatchPage ? "active" : ""}`} to="/dispatch">
                                                    Dispatch
                                                </Link>
                                            </li>
                                        )}
                                        <li>
                                            <Link className="dropdown-item" to="/drivers">
                                                Driver List
                                            </Link>
                                        </li>
                                        {hasPermission("vehicle-view") && (
                                            <>
                                                <li>
                                                    <Link className="dropdown-item" to="/vehicles">
                                                        Vehicles
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link className="dropdown-item" to="/vehicles/create">
                                                        Create Vehicle
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                        <li>
                                            <Link className="dropdown-item" to="/drivers/performance">
                                                Performance
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/drivers/statistics/signups/by_week">
                                                Signups by Week
                                            </Link>
                                        </li>
                                        {hasPermission("driver-update") && (
                                            <>
                                                <li>
                                                    <Link className="dropdown-item" to="/drivers/create">
                                                        Create Driver
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link className="dropdown-item" to="/drivers/notify-all">
                                                        Notify All
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link className="dropdown-item" to="/drivers/payout-reports">
                                                        Payout Reports
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </li>
                            )}
                            {hasPermission("user-view") && (
                                <li className="nav-item dropdown">
                                    <a
                                        className={`nav-link dropdown-toggle ${location.pathname.startsWith("/users") ? "active" : ""}`}
                                        href="#"
                                        id="navbarDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Customers
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        <li>
                                            <Link className="dropdown-item" to="/users">
                                                Customer List
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/users/performance">
                                                Performance
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/users/statistics/signups/by_week">
                                                Signups by Week
                                            </Link>
                                        </li>
                                        {hasPermission("user-update") && (
                                            <>
                                                <li>
                                                    <Link className="dropdown-item" to="/users/create">
                                                        Create Customer
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link className="dropdown-item" to="/users/notify-all">
                                                        Notify All
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </li>
                            )}
                            {hasPermission("invoice-view") && (
                                <li className="nav-item">
                                    <Link
                                        className={`nav-link ${location.pathname.startsWith("/invoices") ? "active" : ""}`}
                                        to="/invoices"
                                    >
                                        Invoices
                                    </Link>
                                </li>
                            )}
                            {hasPermission("promo-code-view") && (
                                <li className="nav-item dropdown">
                                    <a
                                        className={`nav-link dropdown-toggle ${location.pathname.startsWith("/promo-codes") ? "active" : ""}`}
                                        href="#"
                                        id="navbarDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Promo
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        <li>
                                            <Link className="dropdown-item" to="/promo-codes">
                                                Promo Code List
                                            </Link>
                                        </li>
                                        {hasPermission("promo-code-update") && (
                                            <>
                                                <li>
                                                    <Link className="dropdown-item" to="/promo-codes/create">
                                                        Create Promo Code
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </li>
                            )}
                            {hasPermission("cargo-insurance-view") && (
                                <li className="nav-item admin-secondary-item admin-priority-1">
                                    <Link className={`nav-link ${location.pathname.startsWith("/cargo-insurances") ? "active" : ""}`} to="/cargo-insurances">
                                        Insurances
                                    </Link>
                                </li>
                            )}
                            {hasPermission("truck-type-view") && (
                                <li className="nav-item admin-secondary-item admin-priority-2">
                                    <Link className={`nav-link ${location.pathname.startsWith("/truck-types/prices") ? "active" : ""}`} to="/truck-types/prices">
                                        Prices
                                    </Link>
                                </li>
                            )}
                            {hasPermission("tax-rate-view") && (
                                <li className="nav-item admin-secondary-item admin-priority-3">
                                    <Link className={`nav-link ${location.pathname.startsWith("/tax-rates") ? "active" : ""}`} to="/tax-rates">
                                        Taxes
                                    </Link>
                                </li>
                            )}
                            {hasPermission("contact-view") && (
                                <li className="nav-item admin-secondary-item admin-priority-4">
                                    <Link className={`nav-link ${location.pathname.startsWith("/contacts") ? "active" : ""}`} to="/contacts">
                                        Contacts
                                    </Link>
                                </li>
                            )}
                            {hasPermission("device-view") && (
                                <li className="nav-item dropdown admin-secondary-item admin-priority-5">
                                    <button
                                        className={`nav-link dropdown-toggle border-0 bg-transparent ${location.pathname.startsWith("/devices") ? "active" : ""}`}
                                        type="button"
                                        id="devicesDropdown"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Devices
                                    </button>
                                    <ul className="dropdown-menu" aria-labelledby="devicesDropdown">
                                        <li>
                                            <Link className={`dropdown-item ${location.pathname.replace(/\/+$/, '') === "/devices" ? "active" : ""}`} to="/devices">
                                                Device List
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className={`dropdown-item ${location.pathname.replace(/\/+$/, '') === "/devices/statistics" ? "active" : ""}`} to="/devices/statistics">
                                                Statistics
                                            </Link>
                                        </li>
                                    </ul>
                                </li>
                            )}
                            {hasPermission("setting-view") && (
                                <li className="nav-item dropdown admin-secondary-item admin-priority-6">
                                    <a className={`nav-link dropdown-toggle ${location.pathname.startsWith("/settings") || location.pathname.startsWith("/services") ? "active" : ""}`}
                                       href="#"
                                       id="coworkerDropdown"
                                       role="button"
                                       data-toggle="dropdown"
                                       aria-expanded="false"
                                    >
                                        Settings
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        <li><Link className="dropdown-item" to="/settings">Settings</Link></li>
                                        <li><Link className="dropdown-item" to="/services/websockets/test">Test
                                            WebSockets</Link></li>
                                    </ul>
                                </li>
                            )}
                            {hasPermission("admin-log-view") && (
                                <li className="nav-item admin-secondary-item admin-priority-8">
                                    <Link className={`nav-link ${location.pathname.startsWith("/admin-logs") ? "active" : ""}`} to="/admin-logs">
                                        Logs
                                    </Link>
                                </li>
                            )}
                            {hasRole("super-admin") && (
                                <li className="nav-item dropdown admin-secondary-item admin-priority-9">
                                    <a className={`nav-link dropdown-toggle ${location.pathname.startsWith("/coworkers") ? "active" : ""}`}
                                        href="#"
                                        id="coworkerDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Coworkers
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        <li><Link className="dropdown-item" to="/coworkers">Coworkers</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/create">Create Coworker</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/roles">Roles</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/roles/create">Create Role</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/permissions">Permissions</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/permissions/create">Create Permission</Link></li>
                                    </ul>
                                </li>
                            )}
                        </ul>
                        {/* Admin profile and logout dropdown */}
                        <ul className="navbar-nav ml-auto admin-profile-menu">
                            <li className="nav-item dropdown">
                                <a
                                    className="nav-link dropdown-toggle d-flex align-items-center"
                                    href="#"
                                    id="adminDropdown"
                                    role="button"
                                    data-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    {admin?.avatar ? (
                                        <span style={{
                                            display: "inline-block",
                                            width: "30px",
                                            height: "30px",
                                            borderRadius: "50%",
                                            backgroundImage: `url(${admin.avatar})`,
                                            backgroundPosition: "center",
                                            backgroundRepeat: "no-repeat",
                                            backgroundSize: "cover",
                                        }} />
                                    ) : (
                                        <span className="default-avatar">{getInitials(admin?.name)}</span>
                                    )}
                                    &nbsp; {admin?.name}
                                </a>
                                <ul className="dropdown-menu dropdown-menu-right" aria-labelledby="adminDropdown">
                                    <li>
                                        <Link className="dropdown-item" to="/my-profile">
                                            My Profile
                                        </Link>
                                    </li>
                                    <li>
                                        <hr className="dropdown-divider"/>
                                    </li>
                                    <li>
                                        <button className="dropdown-item" onClick={onLogout}>
                                            {isLoggingOut ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    &nbsp;Logging out...
                                                </>
                                            ) : (
                                                "Logout"
                                            )}
                                        </button>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                    <div
                        className={`admin-secondary-drawer ${isOpen ? "show" : ""}`}
                        onClick={handleMenuClick}
                    >
                        <ul className="navbar-nav">
                            {hasPermission("cargo-insurance-view") && (
                                <li className="nav-item admin-drawer-item admin-priority-1">
                                    <Link className={`nav-link ${location.pathname.startsWith("/cargo-insurances") ? "active" : ""}`} to="/cargo-insurances">
                                        Insurances
                                    </Link>
                                </li>
                            )}
                            {hasPermission("truck-type-view") && (
                                <li className="nav-item admin-drawer-item admin-priority-2">
                                    <Link className={`nav-link ${location.pathname.startsWith("/truck-types/prices") ? "active" : ""}`} to="/truck-types/prices">
                                        Prices
                                    </Link>
                                </li>
                            )}
                            {hasPermission("tax-rate-view") && (
                                <li className="nav-item admin-drawer-item admin-priority-3">
                                    <Link className={`nav-link ${location.pathname.startsWith("/tax-rates") ? "active" : ""}`} to="/tax-rates">
                                        Taxes
                                    </Link>
                                </li>
                            )}
                            {hasPermission("contact-view") && (
                                <li className="nav-item admin-drawer-item admin-priority-4">
                                    <Link className={`nav-link ${location.pathname.startsWith("/contacts") ? "active" : ""}`} to="/contacts">
                                        Contacts
                                    </Link>
                                </li>
                            )}
                            {hasPermission("device-view") && (
                                <li className="nav-item dropdown admin-drawer-item admin-priority-5">
                                    <button
                                        className={`nav-link dropdown-toggle border-0 bg-transparent ${location.pathname.startsWith("/devices") ? "active" : ""}`}
                                        type="button"
                                        id="secondaryDevicesDropdown"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Devices
                                    </button>
                                    <ul className="dropdown-menu" aria-labelledby="secondaryDevicesDropdown">
                                        <li>
                                            <Link className={`dropdown-item ${location.pathname.replace(/\/+$/, '') === "/devices" ? "active" : ""}`} to="/devices">
                                                Device List
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className={`dropdown-item ${location.pathname.replace(/\/+$/, '') === "/devices/statistics" ? "active" : ""}`} to="/devices/statistics">
                                                Statistics
                                            </Link>
                                        </li>
                                    </ul>
                                </li>
                            )}
                            {hasPermission("setting-view") && (
                                <li className="nav-item dropdown admin-drawer-item admin-priority-6">
                                    <a className={`nav-link dropdown-toggle ${location.pathname.startsWith("/settings") || location.pathname.startsWith("/services") ? "active" : ""}`}
                                       href="#"
                                       id="secondarySettingsDropdown"
                                       role="button"
                                       data-toggle="dropdown"
                                       aria-expanded="false"
                                    >
                                        Settings
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="secondarySettingsDropdown">
                                        <li><Link className="dropdown-item" to="/settings">Settings</Link></li>
                                        <li><Link className="dropdown-item" to="/services/websockets/test">Test
                                            WebSockets</Link></li>
                                    </ul>
                                </li>
                            )}
                            {hasPermission("admin-log-view") && (
                                <li className="nav-item admin-drawer-item admin-priority-7">
                                    <Link className={`nav-link ${location.pathname.startsWith("/admin-logs") ? "active" : ""}`} to="/admin-logs">
                                        Admin Logs
                                    </Link>
                                </li>
                            )}
                            {hasRole("super-admin") && (
                                <li className="nav-item admin-drawer-item admin-priority-8">
                                    <Link className={`nav-link ${location.pathname.startsWith("/whitelabels") ? "active" : ""}`} to="/whitelabels">
                                        Whitelabels
                                    </Link>
                                </li>
                            )}
                            {hasRole("super-admin") && (
                                <li className="nav-item dropdown admin-drawer-item admin-priority-9">
                                    <a className={`nav-link dropdown-toggle ${location.pathname.startsWith("/coworkers") ? "active" : ""}`}
                                        href="#"
                                        id="secondaryCoworkerDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Coworkers
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="secondaryCoworkerDropdown">
                                        <li><Link className="dropdown-item" to="/coworkers">Coworkers</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/create">Create Coworker</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/roles">Roles</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/roles/create">Create Role</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/permissions">Permissions</Link></li>
                                        <li><Link className="dropdown-item" to="/coworkers/permissions/create">Create Permission</Link></li>
                                    </ul>
                                </li>
                            )}
                            <li className="nav-item dropdown admin-profile-menu admin-drawer-profile">
                                <a
                                    className="nav-link dropdown-toggle d-flex align-items-center"
                                    href="#"
                                    id="secondaryAdminDropdown"
                                    role="button"
                                    data-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    {admin?.avatar ? (
                                        <span style={{
                                            display: "inline-block",
                                            width: "30px",
                                            height: "30px",
                                            borderRadius: "50%",
                                            backgroundImage: `url(${admin.avatar})`,
                                            backgroundPosition: "center",
                                            backgroundRepeat: "no-repeat",
                                            backgroundSize: "cover",
                                        }} />
                                    ) : (
                                        <span className="default-avatar">{getInitials(admin?.name)}</span>
                                    )}
                                    &nbsp; {admin?.name}
                                </a>
                                <ul className="dropdown-menu dropdown-menu-right" aria-labelledby="secondaryAdminDropdown">
                                    <li>
                                        <Link className="dropdown-item" to="/my-profile">
                                            My Profile
                                        </Link>
                                    </li>
                                    <li>
                                        <hr className="dropdown-divider"/>
                                    </li>
                                    <li>
                                        <button className="dropdown-item" onClick={onLogout}>
                                            {isLoggingOut ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    &nbsp;Logging out...
                                                </>
                                            ) : (
                                                "Logout"
                                            )}
                                        </button>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={toggle}
                        aria-controls="adminNavbarMenu"
                        aria-expanded={isOpen}
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"/>
                    </button>
                </>
            ) : null}
        </nav>
    );
};

export default Header;
